import { PositionCoords } from '@/shared/types/tooltip';

const OFFSET = 8;
const MARGIN = 8;

export type TooltipPositionInput = {
  position: string;
  alignment: string;
  absolute: boolean;
  triggerRect: DOMRectReadOnly;
  tooltipRect: DOMRectReadOnly;
  containerRect: DOMRectReadOnly | null;
  viewportWidth: number;
  viewportHeight: number;
};

/** Resultado del cálculo: coords, cierre por coords inválidas, o sin op (sin contenedor en absoluto). */
export type TooltipPositionResult =
  | { status: 'noop' }
  | { status: 'close' }
  | {
      status: 'ok';
      coords: PositionCoords;
      /** Solo definido cuando hubo flip en modo absoluto; el hook debe hacer `setAdjustedPosition` en ese caso. */
      flippedAdjustedPosition?: string;
    };

function baseCoordsFixed(
  position: string,
  triggerRect: DOMRectReadOnly,
  tooltipRect: DOMRectReadOnly,
  offset: number
): PositionCoords {
  let top = 0;
  let left = 0;
  switch (position) {
    case 'top':
      top = triggerRect.top - tooltipRect.height - offset;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case 'bottom':
      top = triggerRect.bottom + offset;
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case 'left':
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.left - tooltipRect.width - offset;
      break;
    case 'right':
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      left = triggerRect.right + offset;
      break;
    case 'top-left':
      top = triggerRect.top - tooltipRect.height - offset;
      left = triggerRect.left;
      break;
    case 'top-right':
      top = triggerRect.top - tooltipRect.height - offset;
      left = triggerRect.right - tooltipRect.width;
      break;
    case 'bottom-left':
      top = triggerRect.bottom + offset;
      left = triggerRect.left;
      break;
    case 'bottom-right':
      top = triggerRect.bottom + offset;
      left = triggerRect.right - tooltipRect.width;
      break;
    case 'right-top':
      top = triggerRect.top;
      left = triggerRect.right + offset;
      break;
    case 'right-bottom':
      top = triggerRect.bottom - tooltipRect.height;
      left = triggerRect.right + offset;
      break;
    case 'left-top':
      top = triggerRect.top;
      left = triggerRect.left - tooltipRect.width - offset;
      break;
    case 'left-bottom':
      top = triggerRect.bottom - tooltipRect.height;
      left = triggerRect.left - tooltipRect.width - offset;
      break;
    default:
      break;
  }
  return { top, left };
}

function baseCoordsAbsolute(
  position: string,
  relativeTop: number,
  relativeLeft: number,
  triggerRect: DOMRectReadOnly,
  tooltipRect: DOMRectReadOnly,
  offset: number
): PositionCoords {
  let top = 0;
  let left = 0;
  switch (position) {
    case 'top':
      top = relativeTop - tooltipRect.height - offset;
      left = relativeLeft + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case 'bottom':
      top = relativeTop + triggerRect.height + offset;
      left = relativeLeft + triggerRect.width / 2 - tooltipRect.width / 2;
      break;
    case 'left':
      top = relativeTop + triggerRect.height / 2 - tooltipRect.height / 2;
      left = relativeLeft - tooltipRect.width - offset;
      break;
    case 'right':
      top = relativeTop + triggerRect.height / 2 - tooltipRect.height / 2;
      left = relativeLeft + triggerRect.width + offset;
      break;
    case 'top-left':
      top = relativeTop - tooltipRect.height - offset;
      left = relativeLeft;
      break;
    case 'top-right':
      top = relativeTop - tooltipRect.height - offset;
      left = relativeLeft + triggerRect.width - tooltipRect.width;
      break;
    case 'bottom-left':
      top = relativeTop + triggerRect.height + offset;
      left = relativeLeft;
      break;
    case 'bottom-right':
      top = relativeTop + triggerRect.height + offset;
      left = relativeLeft + triggerRect.width - tooltipRect.width;
      break;
    case 'right-top':
      top = relativeTop;
      left = relativeLeft + triggerRect.width + offset;
      break;
    case 'right-bottom':
      top = relativeTop + triggerRect.height - tooltipRect.height;
      left = relativeLeft + triggerRect.width + offset;
      break;
    case 'left-top':
      top = relativeTop;
      left = relativeLeft - tooltipRect.width - offset;
      break;
    case 'left-bottom':
      top = relativeTop + triggerRect.height - tooltipRect.height;
      left = relativeLeft - tooltipRect.width - offset;
      break;
    default:
      break;
  }
  return { top, left };
}

/**
 * Calcula top/left del tooltip y, en modo absoluto, si hizo falta voltear la posición por espacio.
 */
export function computeTooltipCoords(input: TooltipPositionInput): TooltipPositionResult {
  const {
    position,
    alignment,
    absolute,
    triggerRect,
    tooltipRect,
    containerRect,
    viewportWidth,
    viewportHeight,
  } = input;

  let top = 0;
  let left = 0;
  let flippedAdjustedPosition: string | undefined;

  if (!absolute) {
    const base = baseCoordsFixed(position, triggerRect, tooltipRect, OFFSET);
    top = base.top;
    left = base.left;
  } else {
    if (!containerRect) {
      return { status: 'noop' };
    }

    const relativeTop = triggerRect.top - containerRect.top;
    const relativeLeft = triggerRect.left - containerRect.left;

    const base = baseCoordsAbsolute(
      position,
      relativeTop,
      relativeLeft,
      triggerRect,
      tooltipRect,
      OFFSET
    );
    top = base.top;
    left = base.left;

    if (top < 0) top = 0;
    if (left < 0) left = 0;

    const hasSpace = (() => {
      switch (position) {
        case 'top':
          return relativeTop >= tooltipRect.height + OFFSET;
        case 'bottom':
          return (
            relativeTop + triggerRect.height + OFFSET + tooltipRect.height <= containerRect.height
          );
        case 'left':
          return relativeLeft >= tooltipRect.width + OFFSET;
        case 'right':
          return (
            relativeLeft + triggerRect.width + OFFSET + tooltipRect.width <= containerRect.width
          );
        default:
          return true;
      }
    })();

    if (!hasSpace) {
      let newPosition = position;
      let adjusted = false;

      if (position === 'top') {
        if (
          relativeTop + triggerRect.height + OFFSET + tooltipRect.height <=
          containerRect.height
        ) {
          newPosition = 'bottom';
          adjusted = true;
        }
      } else if (position === 'bottom') {
        if (relativeTop >= tooltipRect.height + OFFSET) {
          newPosition = 'top';
          adjusted = true;
        }
      } else if (position === 'left') {
        if (relativeLeft + triggerRect.width + OFFSET + tooltipRect.width <= containerRect.width) {
          newPosition = 'right';
          adjusted = true;
        }
      } else if (position === 'right') {
        if (relativeLeft >= tooltipRect.width + OFFSET) {
          newPosition = 'left';
          adjusted = true;
        }
      }

      if (adjusted) {
        switch (newPosition) {
          case 'top':
            top = relativeTop - tooltipRect.height - OFFSET;
            left = relativeLeft + triggerRect.width / 2 - tooltipRect.width / 2;
            break;
          case 'bottom':
            top = relativeTop + triggerRect.height + OFFSET;
            left = relativeLeft + triggerRect.width / 2 - tooltipRect.width / 2;
            break;
          case 'left':
            top = relativeTop + triggerRect.height / 2 - tooltipRect.height / 2;
            left = relativeLeft - tooltipRect.width - OFFSET;
            break;
          case 'right':
            top = relativeTop + triggerRect.height / 2 - tooltipRect.height / 2;
            left = relativeLeft + triggerRect.width + OFFSET;
            break;
          default:
            break;
        }
        flippedAdjustedPosition = newPosition;
      }
    }
  }

  if (position.includes('top') || position.includes('bottom')) {
    switch (alignment) {
      case 'start':
        if (absolute && containerRect) {
          left = triggerRect.left - containerRect.left;
        } else {
          left = triggerRect.left;
        }
        break;
      case 'end':
        if (absolute && containerRect) {
          left = triggerRect.right - containerRect.left - tooltipRect.width;
        } else {
          left = triggerRect.right - tooltipRect.width;
        }
        break;
      case 'center':
      default:
        break;
    }
  } else if (position.includes('left') || position.includes('right')) {
    switch (alignment) {
      case 'start':
        if (absolute && containerRect) {
          top = triggerRect.top - containerRect.top;
        } else {
          top = triggerRect.top;
        }
        break;
      case 'end':
        if (absolute && containerRect) {
          top = triggerRect.bottom - containerRect.top - tooltipRect.height;
        } else {
          top = triggerRect.bottom - tooltipRect.height;
        }
        break;
      case 'center':
      default:
        break;
    }
  }

  if (!absolute) {
    if (left < MARGIN) left = MARGIN;
    if (left + tooltipRect.width > viewportWidth - MARGIN) {
      left = viewportWidth - tooltipRect.width - MARGIN;
    }
    if (top < MARGIN) top = MARGIN;
    if (top + tooltipRect.height > viewportHeight - MARGIN) {
      top = viewportHeight - tooltipRect.height - MARGIN;
    }
  } else if (containerRect) {
    if (left < MARGIN) left = MARGIN;
    if (left + tooltipRect.width > containerRect.width - MARGIN) {
      left = containerRect.width - tooltipRect.width - MARGIN;
    }
    if (top < MARGIN) top = MARGIN;
    if (top + tooltipRect.height > containerRect.height - MARGIN) {
      top = containerRect.height - tooltipRect.height - MARGIN;
    }
  }

  if (isNaN(top) || isNaN(left) || top < 0 || left < 0) {
    return { status: 'close' };
  }

  return {
    status: 'ok',
    coords: { top, left },
    flippedAdjustedPosition,
  };
}
